import { KeyboardEvent, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, FormControl, FormLabel, IconButton, Input, Pill, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { INVOICE_LOCKED_CC_EMAIL, INVOICE_SECTIONS } from "@/constants";
import { RecipientSectionProps } from "@/utils/interface";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import SectionCard from "./SectionCard";

const isValidEmail = (email: string) => regex.email.test(email);

const RecipientSection = ({
  disabled,
  value,
  errors,
  expanded,
  onExpandedChange,
  onChange,
  onSave,
}: RecipientSectionProps) => {
  const [ccDraft, setCcDraft] = useState("");

  const addCcEmail = () => {
    const trimmed = ccDraft.trim();
    if (!trimmed || !isValidEmail(trimmed)) return;
    if (trimmed === INVOICE_LOCKED_CC_EMAIL) {
      setCcDraft("");
      return;
    }
    if (value.ccEmails.includes(trimmed)) {
      setCcDraft("");
      return;
    }
    const ccEmails = [...value.ccEmails, trimmed];
    onChange({ ccEmails });
    setCcDraft("");
    onSave({ ccEmails });
  };

  const removeCcEmail = (email: string) => {
    const ccEmails = value.ccEmails.filter((e) => e !== email);
    onChange({ ccEmails });
    onSave({ ccEmails });
  };

  const onCcKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCcEmail();
    }
  };

  return (
    <SectionCard
      title={INVOICE_SECTIONS.recipient}
      subtitle="Who receives the Invoice details on email."
      disabled={disabled}
      collapsible
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <Stack direction="column" rowGap={3}>
        <Stack direction="row" columnGap={3}>
          <Box sx={{ flex: 1 }}>
            <FormControl error={!!errors?.recipientFirstName} required>
              <FormLabel>First name</FormLabel>
              <Input
                placeholder="e.g. John"
                value={value.recipient.firstName}
                disabled={disabled}
                onChange={(e) => onChange({ recipient: { ...value.recipient, firstName: e.target.value } })}
                onBlur={() => onSave({ firstName: value.recipient.firstName })}
              />
              {errors?.recipientFirstName && (
                <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                  {errors.recipientFirstName}
                </Typography>
              )}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl error={!!errors?.recipientLastName} required>
              <FormLabel>Last name</FormLabel>
              <Input
                placeholder="e.g. Doe"
                value={value.recipient.lastName}
                disabled={disabled}
                onChange={(e) => onChange({ recipient: { ...value.recipient, lastName: e.target.value } })}
                onBlur={() => onSave({ lastName: value.recipient.lastName })}
              />
              {errors?.recipientLastName && (
                <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                  {errors.recipientLastName}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Stack>

        <FormControl>
          <FormLabel>Billing name</FormLabel>
          <Input
            placeholder="e.g. Acme Pty Ltd"
            value={value.recipient.billingName || ""}
            disabled={disabled}
            onChange={(e) => onChange({ recipient: { ...value.recipient, billingName: e.target.value } })}
            onBlur={() => onSave({ billingName: value.recipient.billingName || "" })}
          />
        </FormControl>

        <Stack direction="row" columnGap={3}>
          <Box sx={{ flex: 1 }}>
            <FormControl error={!!errors?.recipientEmail} required>
              <FormLabel>Recipient email</FormLabel>
              <Input
                type="email"
                placeholder="ap@company.com"
                value={value.recipient.email}
                disabled={disabled}
                onChange={(e) => onChange({ recipient: { ...value.recipient, email: e.target.value } })}
                onBlur={() => onSave({ email: value.recipient.email })}
              />
              {errors?.recipientEmail && (
                <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
                  {errors.recipientEmail}
                </Typography>
              )}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl>
              <FormLabel>Address</FormLabel>
              <Input
                placeholder="123 Example St, City"
                value={value.recipient.address || ""}
                disabled={disabled}
                onChange={(e) => onChange({ recipient: { ...value.recipient, address: e.target.value } })}
                onBlur={() => onSave({ address: value.recipient.address || "" })}
              />
            </FormControl>
          </Box>
        </Stack>

        <FormControl>
          <FormLabel>Billing address</FormLabel>
          <Input
            placeholder="123 Example St, City"
            value={value.recipient.billingAddress || ""}
            disabled={disabled}
            onChange={(e) => onChange({ recipient: { ...value.recipient, billingAddress: e.target.value } })}
            onBlur={() => onSave({ billingAddress: value.recipient.billingAddress || "" })}
          />
        </FormControl>

        {/* Hide CC email for now need to create custom template for cc emails */}
        <FormControl>
          <FormLabel>CC on invoice email</FormLabel>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              border: "1px solid",
              borderColor: "neutral.300",
              borderRadius: "6px",
              padding: "6px 8px",
              minHeight: 40,
            }}
          >
            {value.ccEmails.map((email) => (
              <Pill intent="neutral" key={email}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {email}
                  <IconButton variant="inline" intent="neutral" size="sm" onClick={() => removeCcEmail(email)} aria-label={`Remove ${email}`}>
                    <CloseIcon style={{ fontSize: "0.85rem" }} />
                  </IconButton>
                </span>
              </Pill>
            ))}
            <Input
              placeholder="another@company.com"
              value={ccDraft}
              onChange={(e) => setCcDraft(e.target.value)}
              onKeyDown={onCcKeyDown}
              onBlur={addCcEmail}
              style={{ flex: 1, minWidth: 180, border: "none", boxShadow: "none" }}
            />
          </Box>
          <Typography level="body-xs" textColor="neutral.500" style={{ marginTop: 4 }}>
            Type and press Enter to add. Financeable billing is always CC'd.
          </Typography>
        </FormControl>
      </Stack>
    </SectionCard>
  );
};

export default RecipientSection;
